import { Box, Button, Typography } from '@mui/material';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMars,
  faVenus,
  faVenusMars,
} from '@fortawesome/free-solid-svg-icons';
import { PeerProfile, PeerProfilePatient } from '../model/PeerProfile';
import parse from 'date-fns/parse';
import { ageInMonth, ageInYear } from '../../profile/component/PatientProfile';
import { useQuickConnectPeerProfile } from '../hook/useQuickConnectPeerProfile';
import { useDiscoverProfileDispatch } from '../provider/DiscoverProfileDispatchProvider';

const PATIENT_TO_SHOW = 2;

type DiscoverCardProps = {
  profile: PeerProfile;
  onSelectProfile: (profileGuid: string) => void;
};

export const DiscoveringProfileCard = ({
  profile,
  onSelectProfile,
}: DiscoverCardProps) => {
  const discoverDispatch = useDiscoverProfileDispatch();
  const quickConnectOperation = useQuickConnectPeerProfile(discoverDispatch);
  const moreChild = useMemo(
    () => Math.max(profile.patients.length - PATIENT_TO_SHOW, 0),
    [profile.patients],
  );
  const connectProfile = useCallback(async () => {
    await quickConnectOperation(profile.guid);
  }, [profile, quickConnectOperation]);

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: 3,
        flex: '0 0 250px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        onClick={() => onSelectProfile(profile.guid)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 5,
          flexGrow: 1,
          flexDirection: 'column',
          background: '#ffffff',
          cursor: 'pointer',
        }}
      >
        <Typography variant='body2' sx={{ mb: 5 }}>
          <FormattedMessage id='community.peers.item.address' />
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', mb: 2 }}>
          {profile.city}
        </Typography>
        <Typography variant='body2' sx={{ mb: 10 }}>
          {`${profile.state} ${profile.zipCode ?? ''}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2.5, mb: 10 }}>
          {profile.patients.slice(0, PATIENT_TO_SHOW).map((patient) => (
            <PatientInfo key={patient.id} patientInfo={patient} />
          ))}
          {moreChild > 0 ? <MorePatient morePatient={moreChild} /> : null}
        </Box>
      </Box>
      <Button
        onClick={connectProfile}
        fullWidth
        sx={{
          p: 2,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          textAlign: 'center',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        <Typography>
          <FormattedMessage id='common.connect' />
        </Typography>
      </Button>
    </Box>
  );
};

type PatientInfoProps = {
  patientInfo: PeerProfilePatient;
};

const PatientInfo = ({ patientInfo }: PatientInfoProps) => {
  const intl = useIntl();
  const age = useMemo(() => {
    if (patientInfo.dateOfBirth) {
      return localizeAge(
        parse(patientInfo.dateOfBirth, 'yyyy-MM-dd', new Date()),
        new Date(),
        intl,
      );
    }
    return null;
  }, [patientInfo.dateOfBirth, intl]);
  const genderIcon =
    patientInfo.gender == 'M'
      ? faMars
      : patientInfo.gender == 'F'
      ? faVenus
      : faVenusMars;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: '#00b2a9',
          p: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <FontAwesomeIcon
          icon={genderIcon}
          color='#fff'
          width='100%'
          height='100%'
          fontSize='20px'
        />
      </Box>
      <Typography whiteSpace='nowrap'>{age}</Typography>
    </Box>
  );
};

const MorePatient = ({ morePatient }: { morePatient: number }) => {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: '#00b2a9',
        p: 2,
        color: 'primary.contrastText',
        textAlign: 'center',
      }}
    >
      {morePatient}+
    </Box>
  );
};

const localizeAge = (birthDate: Date, now: Date, intl: IntlShape) => {
  const yearAge = ageInYear(birthDate, now);
  if (yearAge >= 1) {
    return intl.formatMessage(
      { id: 'community.connections.kids.age-year' },
      { age: yearAge },
    );
  }
  return intl.formatMessage(
    { id: 'community.connections.kids.age-month' },
    { age: ageInMonth(birthDate, now) },
  );
};
